import React, { useState } from 'react';
import { SectionFolder } from '@src/components/SectionFolder';
import { SectionTitle } from '@src/components/SectionTitle';
import { SectionProps } from '../types/Settings';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { SECTION_TITLE_SIZE } from '../utils/variables';
import { SectionContent } from '../subcomponents/SectionContent';
import { TypeIcon } from '../../Icons/DropDownIcons/Type';
import { style } from 'typestyle';
import { Hide } from '@src/components/Hide';
import { ThemeProvider } from '@mui/material';
import { materialTheme } from '../subcomponents/Theme';
import { AutoComplete, RenderTag } from '@src/components/AutoComplete';
import { useQueryCollections } from '@src/queries/collections/useQueryCollections';
import { CollectionView, WidgetView } from 'common';
import { AutoCompleteOption } from '@src/types/AutoComplete';
import { LabelPill } from '@src/components/LabelPill';
import { ArrayHelper } from '@src/services/helpers/array';
import { useQuickType } from '../hooks/useQuickType';
import { Tooltip } from '@mui/material';
import { InfoSvgIcon } from '@src/components/SvgIcons/Info';
import { ConfirmationOverlay } from '../subcomponents/ConfirmationOverlay';

interface QuickTypeProps extends SectionProps {
  remoteWidget: WidgetView;
}

const QuickType = ({ opened, onOpen, editedWidget, setEditedWidget, remoteWidget }: QuickTypeProps) => {
  const { widgetType, handleTypeChange, isCollectionType } = useQuickType(
    editedWidget,
    setEditedWidget,
    remoteWidget
  );
  
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState<'collection' | 'product' | null>(null);

  const { data: allCollections, isLoading: isLoadingCollections } = useQueryCollections({
    enabled: isCollectionType
  });
  const collectionsMap = ArrayHelper.mapToObject(allCollections || [], (c) => c._id);
  
  function renderCollectionTag(id: string, onRemove: () => void) {
    let label: string = '';

    if (allCollections) {
      label = collectionsMap[id]?.name;
    }

    if (!label) {
      return (
        <Tooltip title={lang.tooltips.unauthorizedCollection} key={id}>
          <span>{renderTag(id, lang.fields.unauthorizedCollectionName, onRemove)}</span>
        </Tooltip>
      );
    }
    return renderTag(id, label, onRemove);
  }

  const renderTag = (key: string, label: string, onRemove: () => void) => (
    <LabelPill onRemove={onRemove} key={key}>
      {label}
    </LabelPill>
  );

  function collectionsToOptions(collections: CollectionView[] = []): AutoCompleteOption[] {
    return collections.map((s) => ({ id: s._id, label: s.name }));
  }

  function handleAddCollection(id: string) {
    const currentSources = editedWidget.settings.default_sources || {};
    const currentCollections = currentSources.collection_list || [];
    
    setEditedWidget({
      ...editedWidget,
      settings: {
        ...editedWidget.settings,
        default_sources: {
          ...currentSources,
          collection_list: [...currentCollections, id]
        }
      }
    });
  }

  function handleRemoveCollection(id: string) {
    const currentSources = editedWidget.settings.default_sources || {};
    const currentCollections = [...(currentSources.collection_list || [])];
    
    ArrayHelper.removeOneFrom(currentCollections, id);
    
    setEditedWidget({
      ...editedWidget,
      settings: {
        ...editedWidget.settings,
        default_sources: {
          ...currentSources,
          collection_list: currentCollections
        }
      }
    });
  }
  
  const initiateTypeChange = (type: 'collection' | 'product') => {
    if (type === widgetType) return;
    
    setPendingTypeChange(type);
    setShowConfirmation(true);
  };
  
  const confirmTypeChange = () => {
    if (pendingTypeChange) {
      handleTypeChange(pendingTypeChange);
    }
    setShowConfirmation(false);
    setPendingTypeChange(null);
  };
  
  const cancelTypeChange = () => {
    setShowConfirmation(false);
    setPendingTypeChange(null);
  };

  return (
    <SectionFolder
      title={
        <SectionTitle size={SECTION_TITLE_SIZE}>
          <TypeIcon />
          {lang.title.main}
        </SectionTitle>
      }
      opened={opened}
      onOpen={onOpen}
      afterTitle={
        <Tooltip
          title={lang.tooltips.typeInfo}
          placement='top'
        >
          <span className={infoTypeClass}>
            <InfoSvgIcon />
          </span>
        </Tooltip>
      }
    >
      <SectionContent>
        <div className={wrapperStyle}>
          {showConfirmation ? (
            <ConfirmationOverlay
              message={pendingTypeChange === 'collection' 
                ? lang.confirm.switchToCollection 
                : lang.confirm.switchToProduct}
              onCancel={cancelTypeChange}
              onConfirm={confirmTypeChange}
              cancelText={lang.confirm.cancel}
              confirmText={lang.confirm.confirm}
            />
          ) : (
            <div className={buttonContainerStyle}>
              <button 
                className={getButtonStyle(widgetType === 'collection')}
                onClick={() => initiateTypeChange('collection')}
              >
                {lang.buttons.collection}
              </button>
              <button 
                className={getButtonStyle(widgetType === 'product')}
                onClick={() => initiateTypeChange('product')}
              >
                {lang.buttons.product}
              </button>
            </div>
          )}
        </div>
      </SectionContent>
      
      <Hide hide={!isCollectionType}>
        <SectionContent>
          <ThemeProvider theme={materialTheme}>
            <AutoComplete
              id='collections'
              title={lang.fields.collectionPlaceholder}
              loading={isLoadingCollections}
              onAdd={handleAddCollection}
              onRemove={handleRemoveCollection}
              options={collectionsToOptions(allCollections)}
              selected={editedWidget.settings.default_sources?.collection_list}
              renderTag={renderCollectionTag}
            />
          </ThemeProvider>
        </SectionContent>
      </Hide>
    </SectionFolder>
  );
};

const lang = LocalizedStrings({
  en: {
    title: {
      main: 'Type',
    },
    buttons: {
      collection: 'Collections',
      product: 'Products'
    },
    tooltips: {
      typeInfo: "Choose if your widget will display content based on collections or products",
      unauthorizedCollection: "To modify this collection, request access to the collection from the widget owner",
    },
    fields: {
      collectionPlaceholder: 'choose a collection',
      unauthorizedCollectionName: 'Private collection',
    },
    confirm: {
      switchToCollection: 'Switching to Collection type will remove any Product sources you have configured.',
      switchToProduct: 'Switching to Product type will reset your Collection sources.',
      cancel: 'CANCEL',
      confirm: 'CONFIRM',
    }
  },
});

const buttonContainerStyle = style({
  display: 'flex',
  justifyContent: 'space-around',
  width: '100%',
});

const getButtonStyle = (isSelected: boolean) => style({
  background: '#343434',
  border: `2px solid ${isSelected ? '#2600FF' : '#343434'}`,
  borderRadius: '8px',
  color: 'white',
  fontSize: '0.9em',
  textTransform: 'uppercase',
  padding: '8px 10px',
  cursor: 'pointer',
  minWidth: '130px',
  transition: 'border-color 0.3s ease',
  $nest: {
    '&:hover': {
      borderColor: isSelected ? '#2600FF' : '#4a4a4a'
    }
  }
});

const infoTypeClass = style({
  marginLeft: 10,
  color: '#A0A0A0',
});

const wrapperStyle = style({
  position: 'relative',
  width: '100%'
});

export default QuickType;
